<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-button
        v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_support"
        @click.native="onShowFastTrackPopup"
      >
        Fast track
      </app-button>
      <app-cells>
        <h2 class="title">Upcoming</h2>
      </app-cells>
      <template v-if="upcoming_transfers.length">
        <div v-for="transfer_item in upcoming_transfers" :key="`upcoming_${transfer_item.name}`" style="margin-bottom: 30px">
          <app-cells v-if="transfer_item.name" class="sticky-title">
            <h3 class="title">{{ transfer_item.name }}</h3>
            <div v-if="transfer_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
          </app-cells>
          <div class="transfer-list">
            <transfers-blocks
              v-for="(transfer, index) in transfer_item.items"
              :key="`upcoming_${transfer.id}`"
              @onOpenEditPopup="onOpenEditPopup"
              @onCancelledTransfer="onCancelledTransfer"
              @onDeleteTransfer="onDeleteTransfer"
              :transfer="transfer"
              :count="index + 1"
              :is_show_arrange_button="false"
              :is_show_edit_cancel_buttons="transfer.type.code === 'arrival' || transfer.type.code === 'departure' || transfer.type.code === 'other'"
            />
          </div>
        </div>
      </template>
      <div v-else class="no-results">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No Results</div>
        <div class="no-results__text">There’s no transfers yet</div>
      </div>
      <template v-if="archive_transfers.length">
        <app-cells>
          <h2 class="title">Archive</h2>
        </app-cells>
        <div v-for="transfer_item in archive_transfers" :key="`archive_${transfer_item.name}`" style="margin-bottom: 30px">
          <app-cells v-if="transfer_item.name" class="sticky-title">
            <h3 class="title">{{ transfer_item.name }}</h3>
            <div v-if="transfer_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
          </app-cells>
          <div class="transfer-list">
            <transfers-blocks
              v-for="(transfer, index) in transfer_item.items"
              :key="`archive_${transfer.id}`"
              :transfer="transfer"
              :count="index + 1"
              :is_show_arrange_button="false"
              :is_show_edit_cancel_buttons="false"
            />
          </div>
        </div>
      </template>
      <template v-if="cancelled_transfers.length">
        <app-cells>
          <h2 class="title">Cancelled</h2>
        </app-cells>
        <div v-for="transfer_item in cancelled_transfers" :key="`cancelled_${transfer_item.name}`" style="margin-bottom: 30px">
          <app-cells v-if="transfer_item.name" class="sticky-title">
            <h3 class="title">{{ transfer_item.name }}</h3>
            <div v-if="transfer_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
          </app-cells>
          <div class="transfer-list">
            <transfers-blocks
              v-for="(transfer, index) in transfer_item.items"
              :key="`cancelled_${transfer.id}`"
              :transfer="transfer"
              :count="index + 1"
              :is_show_arrange_button="false"
              :is_show_edit_cancel_buttons="false"
            />
          </div>
        </div>
      </template>
    </template>
    <modal
        name="arrival-modal"
        :width="600"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('arrival-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit transfer</div>
        <div v-if="arrival_form" class="popup__body">
          <form @submit.prevent="onCheckEditArrivalForm">
            <div class="arrival-popup">
              <div class="arrival-popup__name">{{ arrival_form.name }} - {{ arrival_form.pax }} Pax</div>
              <div class="arrival-popup__info">
                <div class="arrival-popup__row">
                  <span>Pick-up date:</span>
                  <span>
                    {{ dateFormat(arrival_form.pick_up_date) }}<i class="arrival-popup__divider">|</i>{{ arrival_form.pick_up_time }}
                  </span>
                </div>
                <div class="arrival-popup__row">
                  <span>Pick-up point:</span>
                  <span>{{ arrival_form.pick_up_point.short_name }}</span>
                </div>
                <div class="arrival-popup__row">
                  <span>Transport:</span>
                  <span>{{ arrival_form.transport_method.name }}<i v-if="arrival_form.transport_company" class="arrival-popup__divider">|</i>{{ arrival_form.transport_company ? arrival_form.transport_company.name : '' }}<i class="arrival-popup__divider">|</i>{{ arrival_form.transport_flight_number || '' }}<i v-if="arrival_form.transport_flight_number" class="arrival-popup__divider">|</i>{{ dateFormat(arrival_form.transport_date) }}<i class="arrival-popup__divider">|</i>{{ arrival_form.transport_time }}</span>
                </div>
              </div>
              <draggable
                  :list="arrival_form.points"
                  v-bind="dragOptions"
                  handle=".sortable__handle"
                  class="transfer-sortable transfer-sortable--lite"
              >
                <transition-group type="transition" :name="'flip-list'" tag="div">
                  <div v-for="(element, index) in arrival_form.points" :key="`arrival_points_${index}`" class="transfer-sortable__item">
                    <div class="transfer-sortable__row">
                      <app-form-group :label="`Destination ${index + 1}`" class="form-group--no-indent" required>
                        <app-select
                          @input="onLocationSelected(element.location, index, 'arrival')"
                          @search="onLocationSearch"
                          v-model="element.location"
                          :options="location_options"
                          :filterable="false"
                          :clearable="false"
                          placeholder="Select"
                          :error="$v.arrival_form.points.$each[index].location.$error"
                        />
                        <template #error>
                          <div v-if="$v.arrival_form.points.$each[index].location.$dirty && !$v.arrival_form.points.$each[index].location.required">Required field</div>
                        </template>
                      </app-form-group>
                      <button type="button" class="transfer-sortable__btn sortable__handle">
                        <img src="@/assets/img/sortable-arrow.svg" alt="Arrow">
                      </button>
                      <button
                          v-if="index !== 0"
                          @click="onRemoveDestination('arrival', index)"
                          class="transfer-sortable__close"
                          type="button"
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="Delete">
                      </button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <app-cells>
                <app-button @click.native="onAddDestination('arrival')" size="small">
                  <img src="@/assets/img/plus-icon.svg" alt="Add">
                  <span>Add destination</span>
                </app-button>
              </app-cells>
              <app-form-group label="Signboard" label-for="signboard" required>
                <app-textarea
                    v-model="arrival_form.signboard"
                    id="signboard"
                    placeholder="Signboard"
                    :error="$v.arrival_form.signboard.$error"
                    :readonly="arrival_form.is_signboard_printed"
                />
                <template #error>
                  <div v-if="$v.arrival_form.signboard.$dirty && !$v.arrival_form.signboard.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Notes" label-for="notes">
                <app-textarea
                    v-model="arrival_form.notes"
                    id="notes"
                    placeholder="Notes"
                />
              </app-form-group>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="arrival_submit">Save transfer</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
        name="departure-modal"
        :width="655"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('departure-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit transfer</div>
        <div v-if="departure_form" class="popup__body">
          <form @submit.prevent="onCheckEditDepartureForm">
            <div class="arrival-popup">
              <div class="arrival-popup__name">{{ departure_form.name }} - {{ departure_form.pax }} Pax</div>
              <div class="arrival-popup__info">
                <div class="arrival-popup__row">
                  <span>Transport:</span>
                  <span>{{ departure_form.transport_method.name }}<i v-if="departure_form.transport_company" class="arrival-popup__divider">|</i>{{ departure_form.transport_company ? departure_form.transport_company.name : '' }}<i class="arrival-popup__divider">|</i>{{ departure_form.transport_flight_number || '' }}<i v-if="departure_form.transport_flight_number" class="arrival-popup__divider">|</i>{{ dateFormat(departure_form.transport_date) }}<i class="arrival-popup__divider">|</i>{{ departure_form.transport_time }}</span>
                </div>
              </div>
              <app-grid>
                <template #item-1>
                  <app-form-group label="Pick-up date" required>
                    <app-date-picker
                      v-model="departure_form.pick_up_date"
                      placeholder="Specify a date"
                      :error="$v.departure_form.pick_up_date.$error"
                    />
                    <template #error>
                      <div v-if="$v.departure_form.pick_up_date.$dirty && !$v.departure_form.pick_up_date.required">Required field</div>
                      <div v-if="$v.departure_form.pick_up_date.$dirty && !$v.departure_form.pick_up_date.minValue">
                        Date cannot be less than the current date
                      </div>
                    </template>
                  </app-form-group>
                </template>
                <template #item-2>
                  <app-form-group label="Pick-up time" label-for="pick_up_time" required>
                    <app-input
                        v-model="departure_form.pick_up_time"
                        v-mask="'99:99'"
                        placeholder="Time"
                        autocomplete="off"
                        id="pick_up_time-time"
                        name="pick_up_time"
                        @paste.native.prevent
                        :error="$v.departure_form.pick_up_time.$error"
                    />
                    <template #error>
                      <div v-if="$v.departure_form.pick_up_time.$dirty && !$v.departure_form.pick_up_time.required">Required field</div>
                      <div v-if="$v.departure_form.pick_up_time.$dirty && !$v.departure_form.pick_up_time.timeRegex">Wrong time</div>
                    </template>
                  </app-form-group>
                </template>
              </app-grid>
              <app-form-group label="Pick-up point" required>
                <app-select
                  @input="onPickUpPointSelected(departure_form.pick_up_point, 'departure')"
                  @search="onLocationSearch"
                  v-model="departure_form.pick_up_point"
                  :options="location_options"
                  :filterable="false"
                  :clearable="false"
                  placeholder="Select"
                  :error="$v.departure_form.pick_up_point.$error"
                />
                <template #error>
                  <div v-if="$v.departure_form.pick_up_point.$dirty && !$v.departure_form.pick_up_point.required">Required field</div>
                </template>
              </app-form-group>
              <draggable
                  :list="departure_form.points"
                  v-bind="dragOptions"
                  handle=".sortable__handle"
                  class="transfer-sortable transfer-sortable--lite"
              >
                <transition-group type="transition" :name="'flip-list'" tag="div">
                  <div v-for="(element, index) in departure_form.points" :key="`departure_points_${index}`" class="transfer-sortable__item">
                    <div class="transfer-sortable__row">
                      <app-form-group :label="`Destination ${index + 1}`" class="form-group--no-indent" required>
                        <app-select
                          @input="onLocationSelected(element.location, index, 'departure')"
                          @search="onLocationSearch"
                          v-model="element.location"
                          :options="location_options"
                          :filterable="false"
                          :clearable="false"
                          placeholder="Select"
                          :error="$v.departure_form.points.$each[index].location.$error"
                        />
                        <template #error>
                          <div v-if="$v.departure_form.points.$each[index].location.$dirty && !$v.departure_form.points.$each[index].location.required">Required field</div>
                        </template>
                      </app-form-group>
                      <button type="button" class="transfer-sortable__btn sortable__handle">
                        <img src="@/assets/img/sortable-arrow.svg" alt="Arrow">
                      </button>
                      <button
                          v-if="index !== 0"
                          @click="onRemoveDestination('departure', index)"
                          class="transfer-sortable__close"
                          type="button"
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="Delete">
                      </button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <app-cells>
                <app-button @click.native="onAddDestination('departure')" size="small">
                  <img src="@/assets/img/plus-icon.svg" alt="Add">
                  <span>Add destination</span>
                </app-button>
              </app-cells>
              <app-form-group label="Notes" label-for="notes">
                <app-textarea
                    v-model="departure_form.notes"
                    id="notes"
                    placeholder="Notes"
                />
              </app-form-group>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="departure_submit">Save transfer</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
        name="other-modal"
        :width="1100"
        :height="'auto'"
        :scrollable="true"
        draggable=".popup__title"
        :reset="true"
        :clickToClose="false"
        class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('other-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit transfer</div>
        <div v-if="other_form" class="popup__body">
          <form @submit.prevent="onCheckEditOtherForm">
            <div class="arrival-popup">
              <app-grid>
                <template #item-1>
                  <app-form-group label="Name" label-for="tr_name" required>
                    <app-input
                        v-model="other_form.name"
                        placeholder="Name"
                        id="name"
                        name="tr_name"
                        :error="$v.other_form.name.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.name.$dirty && !$v.other_form.name.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-grid>
                    <template #item-1>
                      <app-form-group label="Pick-up date" required>
                        <app-date-picker
                          v-model="other_form.pick_up_date"
                          placeholder="Specify a date"
                          :error="$v.other_form.pick_up_date.$error"
                        />
                        <template #error>
                          <div v-if="$v.other_form.pick_up_date.$dirty && !$v.other_form.pick_up_date.required">Required field</div>
                          <div v-if="$v.other_form.pick_up_date.$dirty && !$v.other_form.pick_up_date.minValue">
                            Date cannot be less than the current date
                          </div>
                        </template>
                      </app-form-group>
                    </template>
                    <template #item-2>
                      <app-form-group label="Pick-up time" label-for="pick_up_time" required>
                        <app-input
                            v-model="other_form.pick_up_time"
                            v-mask="'99:99'"
                            placeholder="Time"
                            autocomplete="off"
                            id="pick_up_time-time"
                            name="pick_up_time"
                            @paste.native.prevent
                            :error="$v.other_form.pick_up_time.$error"
                        />
                        <template #error>
                          <div v-if="$v.other_form.pick_up_time.$dirty && !$v.other_form.pick_up_time.required">Required field</div>
                          <div v-if="$v.other_form.pick_up_time.$dirty && !$v.other_form.pick_up_time.timeRegex">Wrong time</div>
                        </template>
                      </app-form-group>
                    </template>
                  </app-grid>
                  <app-form-group label="Pick-up point" required>
                    <app-select
                      @input="onPickUpPointSelected(other_form.pick_up_point, 'other')"
                      @search="onLocationSearch"
                      v-model="other_form.pick_up_point"
                      :options="location_options"
                      :filterable="false"
                      :clearable="false"
                      placeholder="Select"
                      :error="$v.other_form.pick_up_point.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.pick_up_point.$dirty && !$v.other_form.pick_up_point.required">Required field</div>
                    </template>
                  </app-form-group>
                  <draggable
                      :list="other_form.points"
                      v-bind="dragOptions"
                      handle=".sortable__handle"
                      class="transfer-sortable transfer-sortable--lite"
                  >
                    <transition-group type="transition" :name="'flip-list'" tag="div">
                      <div v-for="(element, index) in other_form.points" :key="`other_points_${index}`" class="transfer-sortable__item">
                        <div class="transfer-sortable__row">
                          <app-form-group :label="`Destination ${index + 1}`" class="form-group--no-indent" required>
                            <app-select
                              @input="onLocationSelected(element.location, index, 'other')"
                              @search="onLocationSearch"
                              v-model="element.location"
                              :options="location_options"
                              :filterable="false"
                              :clearable="false"
                              placeholder="Select"
                              :error="$v.other_form.points.$each[index].location.$error"
                            />
                            <template #error>
                              <div v-if="$v.other_form.points.$each[index].location.$dirty && !$v.other_form.points.$each[index].location.required">Required field</div>
                            </template>
                          </app-form-group>
                          <button type="button" class="transfer-sortable__btn sortable__handle">
                            <img src="@/assets/img/sortable-arrow.svg" alt="Arrow">
                          </button>
                          <button
                              v-if="index !== 0"
                              @click="onRemoveDestination('other', index)"
                              class="transfer-sortable__close"
                              type="button"
                          >
                            <img src="@/assets/img/delete-transfer.svg" alt="Delete">
                          </button>
                        </div>
                      </div>
                    </transition-group>
                  </draggable>
                  <app-cells>
                    <app-button @click.native="onAddDestination('other')" size="small">
                      <img src="@/assets/img/plus-icon.svg" alt="Add">
                      <span>Add destination</span>
                    </app-button>
                  </app-cells>
                </template>
                <template #item-2>
                  <app-form-group label="Clients">
                    <app-select
                      @search="onClientsSearch"
                      v-model="other_form.clients"
                      :options="clients_options"
                      label="full_name"
                      placeholder="Clients"
                      multiple
                    />
                  </app-form-group>
                </template>
              </app-grid>
              <app-form-group label="Notes" label-for="notes">
                <app-textarea
                    v-model="other_form.notes"
                    id="notes"
                    placeholder="Notes"
                />
              </app-form-group>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="other_submit">Save transfer</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
        name="accept-modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to cancelled this transfer?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="cancelTransfer" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
        name="accept-delete-modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to deleted this transfer?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="deleteTransfer" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="fast-track"
      :width="500"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('fast-track')" class="popup__close" type="button"></button>
        <div class="popup__title">Request fast track</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFastTrackForm">
            <app-form-group label="Group name" label-for="group_name" required>
              <app-input
                v-model="form_fast.name"
                @change.native="$v.form_fast.name.$touch()"
                :error="$v.form_fast.name.$error"
                id="group_name"
                placeholder="Group name"
                name="group_name"
              />
              <template #error>
                <div v-if="$v.form_fast.name.$dirty && !$v.form_fast.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Airport" required>
              <app-select
                @input="onAirportSelected"
                @search="onLocationSearch"
                v-model="form_fast.location"
                :options="location_options"
                :clearable="false"
                placeholder="Location"
                :error="$v.form_fast.location.$error"
              />
              <template #error>
                <div v-if="$v.form_fast.location.$dirty && !$v.form_fast.location.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Arrival transfer">
              <app-select
                v-model="form_fast.arrival_transfer"
                :options="arrival_transfers_options"
                :reduce="item => item.id"
                :display-field="option => `${dateFormat(option.pick_up_date)} ${option.pick_up_time} ${option.pick_up_point.short_name}->${option.points.map(item => item.location.short_name).join('->')}`"
                placeholder="Arrival transfer"
              />
            </app-form-group>
            <app-form-group label="Departure transfer">
              <app-select
                v-model="form_fast.departure_transfer"
                :options="departure_transfers_options"
                :reduce="item => item.id"
                :display-field="option => `${dateFormat(option.pick_up_date)} ${option.pick_up_time} ${option.pick_up_point.short_name}->${option.points.map(item => item.location.short_name).join('->')}`"
                placeholder="Departure transfer"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form_fast.$error" type="submit" ref="submit_fast">Request</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import TransfersBlocks from '@/views/transfer/transfers/TransfersBlocks'
import {
  deleteTransfer,
  getClientsLite,
  getGoogleSearch,
  getTransfersDatesList,
  getTransfersList,
  patchArrivalTransfer,
  patchDepartureTransfer,
  patchOtherTransfer,
  patchTransfers,
  postLocation,
  postTransfersFastTrack,
} from '@/http'
import { format, parse } from 'date-fns'
import { helpers, required } from 'vuelidate/lib/validators'
import draggable from 'vuedraggable'
import debounce from 'lodash/debounce'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'ClientsGroupsTransfers',
  components: { TransfersBlocks, draggable },
  props: {
    groupData: Object,
  },
  data() {
    return {
      page_loaded: false,
      upcoming_transfers: [],
      archive_transfers: [],
      cancelled_transfers: [],
      location_options: [],
      clients_options: [],
      arrival_form: null,
      departure_form: null,
      other_form: null,
      current_transfer_id: null,
      form_fast: {},
      arrival_transfers_options: [],
      departure_transfers_options: [],
    }
  },
  validations: {
    arrival_form: {
      points: {
        $each: {
          location: { required },
        }
      },
      signboard: { required }
    },
    departure_form: {
      points: {
        $each: {
          location: { required },
        }
      },
      pick_up_date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      pick_up_time: {
        required,
        timeRegex
      },
      pick_up_point: { required }
    },
    other_form: {
      points: {
        $each: {
          location: { required },
        }
      },
      pick_up_date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      pick_up_time: {
        required,
        timeRegex
      },
      pick_up_point: { required },
      name: { required },
    },
    form_fast: {
      name: { required },
      location: { required },
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: 'destination',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_support() {
      return this.$store.state.userGroups.map(item => item.code).includes('support')
    },
  },
  created() {
    this.fetchItems()
    getTransfersList({
      client_group: this.$route.params.id,
      category: 'upcoming',
      status_exclude: 'cancelled',
      type: 'arrival'
    }).then(res => { this.arrival_transfers_options = res.data })
    getTransfersList({
      client_group: this.$route.params.id,
      category: 'upcoming',
      status_exclude: 'cancelled',
      type: 'departure'
    }).then(res => { this.departure_transfers_options = res.data })
  },
  methods: {
    onCheckFastTrackForm() {
      this.$v.form_fast.$touch()
      if (this.$v.form_fast.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFastForm()
      }
    },
    sendFastForm() {
      this.$refs.submit_fast.preload = true
      postTransfersFastTrack({ ...this.form_fast, location: this.form_fast.location.id })
        .finally(() => { this.$refs.submit_fast.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Fast track requested',
          })
          this.$modal.hide('fast-track')
        })

    },
    onAirportSelected({ name, place_id }) {
      postLocation({ name, place_id }).then(res => {
        this.form_fast.location = res.data
      })
    },
    onShowFastTrackPopup() {
      this.form_fast = { name: this.groupData.name }
      this.$v.form_fast.$reset()
      this.$modal.show('fast-track')
    },
    onCancelledTransfer(id) {
      this.current_transfer_id = id
      this.$modal.show('accept-modal')
    },
    cancelTransfer() {
      this.$refs.delete.preload = true
      patchTransfers(this.current_transfer_id, { status: 'cancelled' })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transfer cancelled',
          })
          this.fetchItems()
          this.$modal.hide('accept-modal')
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onDeleteTransfer(id) {
      this.current_transfer_id = id
      this.$modal.show('accept-delete-modal')
    },
    deleteTransfer() {
      this.$refs.delete.preload = true
      deleteTransfer(this.current_transfer_id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transfer deleted',
          })
          this.fetchItems()
          this.$modal.hide('accept-delete-modal')
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onCheckEditOtherForm() {
      this.$v.other_form.$touch()
      if (this.$v.other_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendOtherForm()
      }
    },
    sendOtherForm() {
      this.$refs.other_submit.preload = true
      patchOtherTransfer(this.other_form.id, this.normalizeOtherForm())
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transfer saved',
          })
          this.$modal.hide('other-modal')
          this.fetchItems()
        })
        .catch(() => { this.$refs.other_submit.preload = false })
    },
    normalizeOtherForm() {
      let points = [ ...this.other_form.points ]
      let clients = [ ...this.other_form.clients ]

      points = points.map((item, index) => ({ location: item.location.id, sort: index, id: item.id }))
      clients = clients.map(item => item.id)
      return {
        points,
        clients,
        pick_up_date: this.other_form.pick_up_date,
        pick_up_time: this.other_form.pick_up_time,
        pick_up_point: this.other_form.pick_up_point.id,
        notes: this.other_form.notes,
        name: this.other_form.name,
      }
    },
    onCheckEditDepartureForm() {
      this.$v.departure_form.$touch()
      if (this.$v.departure_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendDepartureForm()
      }
    },
    sendDepartureForm() {
      this.$refs.departure_submit.preload = true
      patchDepartureTransfer(this.departure_form.id, this.normalizeDepartureForm())
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transfer saved',
          })
          this.$modal.hide('departure-modal')
          this.fetchItems()
        })
        .catch(() => { this.$refs.departure_submit.preload = false })
    },
    normalizeDepartureForm() {
      let points = [ ...this.departure_form.points ]

      points = points.map((item, index) => ({ location: item.location.id, sort: index, id: item.id }))
      return {
        points,
        pick_up_date: this.departure_form.pick_up_date,
        pick_up_time: this.departure_form.pick_up_time,
        pick_up_point: this.departure_form.pick_up_point.id,
        notes: this.departure_form.notes
      }
    },
    onCheckEditArrivalForm() {
      this.$v.arrival_form.$touch()
      if (this.$v.arrival_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendArrivalForm()
      }
    },
    sendArrivalForm() {
      this.$refs.arrival_submit.preload = true
      patchArrivalTransfer(this.arrival_form.id, this.normalizeArrivalForm())
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transfer saved',
          })
          this.$modal.hide('arrival-modal')
          this.fetchItems()
        })
        .catch(() => { this.$refs.arrival_submit.preload = false })
    },
    normalizeArrivalForm() {
      let points = [ ...this.arrival_form.points ]

      points = points.map((item, index) => ({ location: item.location.id, sort: index, id: item.id }))
      return { points, signboard: this.arrival_form.signboard, notes: this.arrival_form.notes }
    },
    onClientsSearch(search, loading) {
      loading(true)
      this.searchClient(loading, search, this)
    },
    searchClient: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.clients_options = []
      } else {
        getClientsLite({search}).then(response => {
          vm.clients_options = response.data
          loading(false)
        })
      }
    }, 350),
    onOpenEditPopup(transfer) {
      this[`${transfer.type.code}_form`] = { ...transfer, points: [ ...transfer.points ] }
      this.$modal.show(`${transfer.type.code}-modal`)
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    onLocationSelected({ name, place_id }, index, type) {
      this.$refs[`${type}_submit`].preload = true
      postLocation({ name, place_id }).then(res => {
        this[`${type}_form`].points[index].location = res.data
        this.$refs[`${type}_submit`].preload = false
      })
    },
    onPickUpPointSelected({ name, place_id }, type) {
      this.$refs[`${type}_submit`].preload = true
      postLocation({ name, place_id }).then(res => {
        this[`${type}_form`].pick_up_point = res.data
        this.$refs[`${type}_submit`].preload = false
      })
    },
    onRemoveDestination(type, index) {
      this[`${type}_form`].points.splice(index, 1)
      this.$v[`${type}_form`].points.$touch()
    },
    onAddDestination(type) {
      this[`${type}_form`].points.push({ location: null })
    },
    onLocationSearch(search, loading) {
      loading(true)
      this.searchLocation(loading, search, this)
    },
    searchLocation: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.location_options = []
      } else {
        getGoogleSearch(search).then(response => {
          loading(false)
          vm.location_options = response.data
        })
      }
    }, 350),
    fetchItems() {
      Promise.all([
        getTransfersDatesList({
          client_group: this.$route.params.id,
          category: 'upcoming',
          status_exclude: 'cancelled',
          group_by: 'pick_up_date',
          range_type: 'day',
        }),
        getTransfersDatesList({
          client_group: this.$route.params.id,
          category: 'archive',
          group_by: 'pick_up_date',
          range_type: 'day',
        }),
        getTransfersDatesList({
          client_group: this.$route.params.id,
          status: 'cancelled',
          group_by: 'pick_up_date',
          range_type: 'day',
        }),
      ])
        .then(res => {
          this.upcoming_transfers = res[0].data
          this.archive_transfers = res[1].data
          this.cancelled_transfers = res[2].data
          this.$modal.hide('arrange-popup')
          this.page_loaded = true
        })
    },
  }
}
</script>
